// Right edge of the Calendar view: slim vertical tab strip ("Unscheduled (n)", "Overdue (n)")
// and the 280px panel listing those tasks, which can be dragged onto the calendar.

import clsx from 'clsx';
import { useDroppable } from '@dnd-kit/core';
import { ListTodo, X } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { Tooltip } from '@/components/ui/Tooltip';
import type { CalDropData } from './CalendarDnd';
import { TaskListRow } from './TaskChip';

export type PanelKind = 'unscheduled' | 'overdue';

export function SideStrip({ open, unscheduled, overdue, onToggle }: { open: PanelKind | null; unscheduled: number; overdue: number; onToggle: (k: PanelKind | null) => void }) {
  return (
    <div className="cal-strip">
      <Tooltip label={open ? 'Hide panel' : 'Show unscheduled tasks'} placement="left">
        <button className={clsx('icon-btn sm cal-strip-toggle', { active: !!open })} onClick={() => onToggle(open ? null : 'unscheduled')}>
          <ListTodo size={15} />
        </button>
      </Tooltip>
      <button className={clsx('cal-strip-tab', { active: open === 'unscheduled' })} onClick={() => onToggle(open === 'unscheduled' ? null : 'unscheduled')}>
        <span className="cal-strip-count">{unscheduled}</span> Unscheduled
      </button>
      <span className="cal-strip-sep" />
      <button className={clsx('cal-strip-tab', { active: open === 'overdue' })} onClick={() => onToggle(open === 'overdue' ? null : 'overdue')}>
        <span className={clsx('cal-strip-count', { danger: overdue > 0 })}>{overdue}</span> Overdue
      </button>
    </div>
  );
}

export function SidePanel({ kind, unscheduled, overdue, onKind, onClose }: { kind: PanelKind; unscheduled: Task[]; overdue: Task[]; onKind: (k: PanelKind) => void; onClose: () => void }) {
  const tasks = kind === 'unscheduled' ? unscheduled : overdue;
  const data: CalDropData = { kind: 'unschedule' };
  const { setNodeRef, isOver } = useDroppable({ id: 'cal-panel-unschedule', data, disabled: kind !== 'unscheduled' });
  return (
    <aside className={clsx('cal-panel', { over: isOver })} ref={setNodeRef}>
      <div className="cal-panel-head">
        <div className="cal-panel-tabs">
          <button className={clsx('cal-panel-tab', { active: kind === 'unscheduled' })} onClick={() => onKind('unscheduled')}>
            Unscheduled <span className="cal-panel-count">{unscheduled.length}</span>
          </button>
          <button className={clsx('cal-panel-tab', { active: kind === 'overdue' })} onClick={() => onKind('overdue')}>
            Overdue <span className={clsx('cal-panel-count', { danger: overdue.length > 0 })}>{overdue.length}</span>
          </button>
        </div>
        <button className="icon-btn sm" onClick={onClose} title="Close">
          <X size={14} />
        </button>
      </div>
      <div className="cal-panel-hint">{kind === 'unscheduled' ? 'Drag tasks onto the calendar to schedule them. Drop a task here to clear its dates.' : 'Drag overdue tasks onto a new day to reschedule.'}</div>
      <div className="cal-panel-list">
        {tasks.map((t) => (
          <TaskListRow key={t.id} task={t} idPrefix="panel" showDue={kind === 'overdue'} />
        ))}
        {tasks.length === 0 && <div className="cal-panel-empty">{kind === 'unscheduled' ? 'Every task has a date.' : 'Nothing overdue. Nice!'}</div>}
      </div>
    </aside>
  );
}
